import { useTranslation } from '../../../shared/i18n';
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Input, Modal, Popconfirm, Space, Table, Tag, Typography, message } from 'antd';
import { CopyOutlined, DeleteOutlined, DownloadOutlined, EditOutlined, KeyOutlined, PlusOutlined } from '@ant-design/icons';
import PageHeader from '../../../shared/components/PageHeader';
import sshKeyService from '../../../shared/services/sshKeyService';
import { formatDateTime } from '../../../shared/utils/date';
import { canGenerateInBrowser, checkPublicKeys, generateEd25519 } from '../../../shared/utils/sshKeygen';

const { Text } = Typography;
const ok = (r) => r && r.status === 'success';
const errOf = (r, fallback) => (r && (r.msg || r.error)) || fallback;
const fileName = (name) => (String(name || 'id_ed25519').replace(/[^A-Za-z0-9._-]+/g, '_').slice(0, 64) || 'id_ed25519');

const MySshKeys = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [limit, setLimit] = useState(null);
    const [addOpen, setAddOpen] = useState(false);
    const [genOpen, setGenOpen] = useState(false);
    const [rename, setRename] = useState(null); // item
    const [saving, setSaving] = useState(false);
    const [generated, setGenerated] = useState(null); // { name, privateKey } — shown once, dropped on close
    const [addForm] = Form.useForm();
    const [genForm] = Form.useForm();
    const [renameForm] = Form.useForm();
    const pasted = Form.useWatch('public_key', addForm);

    const load = useCallback(async () => {
        setLoading(true);
        const r = await sshKeyService.list();
        setLoading(false);
        if (ok(r)) {
            setItems(r.data?.items || []);
            setLimit(r.data?.limit ?? null);
        } else {
            message.error(errOf(r, t('Failed to load')));
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const full = limit !== null && limit >= 0 && items.length >= limit;

    const submitAdd = async (v) => {
        setSaving(true);
        const r = await sshKeyService.add({ name: v.name?.trim() || undefined, public_key: v.public_key.trim() });
        setSaving(false);
        if (!ok(r)) { message.error(errOf(r, t('Failed to add'))); return; }
        message.success(t('Added'));
        setAddOpen(false);
        load();
    };

    const submitGenerate = async (v) => {
        const name = v.name?.trim() || `key-${new Date().toISOString().slice(0, 10)}`;
        setSaving(true);
        try {
            let privateKey = null;
            if (await canGenerateInBrowser()) {
                const kp = await generateEd25519(name);
                const r = await sshKeyService.add({ name, public_key: kp.publicKey });
                if (!ok(r)) { message.error(errOf(r, t('Generation failed'))); return; }
                privateKey = kp.privateKey;
            } else {
                const r = await sshKeyService.generate({ name });
                if (!ok(r) || !r.data?.private_key) { message.error(errOf(r, t('Generation failed'))); return; }
                privateKey = r.data.private_key;
            }
            setGenerated({ name, privateKey });
            load();
        } catch (e) {
            message.error(t('Generation failed'));
        } finally {
            setSaving(false);
        }
    };

    const closeGenerate = () => { setGenOpen(false); setGenerated(null); };

    const download = () => {
        const url = URL.createObjectURL(new Blob([generated.privateKey], { type: 'application/x-pem-file' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `${fileName(generated.name)}.pem`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(generated.privateKey);
            message.success(t('Copied'));
        } catch (e) {
            message.error(t('Failed to copy'));
        }
    };

    const submitRename = async (v) => {
        setSaving(true);
        const r = await sshKeyService.rename(rename.id, v.name.trim());
        setSaving(false);
        if (!ok(r)) { message.error(errOf(r, t('Failed to save'))); return; }
        setRename(null);
        load();
    };

    const remove = async (id) => {
        const r = await sshKeyService.remove(id);
        if (!ok(r)) { message.error(errOf(r, t('Deletion failed'))); return; }
        message.success(t('Deleted'));
        load();
    };

    const columns = [
        { title: t('Name'), dataIndex: 'name', ellipsis: true },
        {
            title: t('Fingerprint'),
            dataIndex: 'fingerprint',
            render: (v) => <Text code copyable={{ text: v }} style={{ fontSize: 12 }}>{v}</Text>,
        },
        {
            title: t('Type'),
            dataIndex: 'type',
            width: 170,
            render: (v, r) => <Tag>{String(v || '').replace(/@openssh\.com$/, '')}{r.bits ? ` ${r.bits}` : ''}</Tag>,
        },
        { title: t('Cloud servers'), dataIndex: 'vm_count', width: 90, render: (v) => v || 0 },
        { title: t('Created at'), dataIndex: 'created_at', width: 150, render: (v) => formatDateTime(v) },
        { title: t('Last used'), dataIndex: 'last_used_at', width: 150, render: (v) => formatDateTime(v) },
        {
            title: t('Actions'),
            width: 110,
            render: (_, r) => (
                <Space size={4}>
                    <Button type="text" size="small" icon={<EditOutlined />} onClick={() => setRename(r)} />
                    <Popconfirm title={t('Delete this key?')} description={t('Keys already installed on servers are not affected')} onConfirm={() => remove(r.id)} okText={t('Delete')} okButtonProps={{ danger: true }} cancelText={t('Cancel')}>
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const pasteState = checkPublicKeys(pasted);

    return (
        <div>
            <PageHeader
                title={t('SSH Keys')}
                extra={(
                    <Space>
                        {limit !== null && limit >= 0 && <Text type="secondary">{items.length} / {limit}</Text>}
                        <Button icon={<PlusOutlined />} disabled={full} onClick={() => setAddOpen(true)}>{t('Add')}</Button>
                        <Button type="primary" icon={<KeyOutlined />} disabled={full} onClick={() => { setGenerated(null); setGenOpen(true); }}>{t('Generate')}</Button>
                    </Space>
                )}
            />
            <Card size="small">
                <Table rowKey="id" size="middle" loading={loading} dataSource={items} columns={columns} pagination={false} scroll={{ x: 900 }} />
            </Card>

            <Modal title={t('Add key')} open={addOpen} onCancel={() => setAddOpen(false)} onOk={() => addForm.submit()} confirmLoading={saving} destroyOnClose>
                <Form form={addForm} layout="vertical" onFinish={submitAdd}>
                    <Form.Item name="name" label={t('Name')}>
                        <Input maxLength={64} placeholder={t('Optional')} />
                    </Form.Item>
                    <Form.Item
                        name="public_key"
                        label={t('Public key')}
                        rules={[{ required: true, message: t('Paste a public key') }]}
                        validateStatus={pasted && pasteState !== 'ok' ? 'error' : undefined}
                        help={pasted && pasteState === 'private' ? t('This is a private key. Paste the public key (.pub).') : pasted && pasteState === 'invalid' ? t('Invalid public key') : undefined}
                        style={{ marginBottom: 0 }}
                    >
                        <Input.TextArea autoSize={{ minRows: 3, maxRows: 8 }} placeholder="ssh-ed25519 AAAA… user@host" />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={t('Generate key')}
                open={genOpen}
                onCancel={closeGenerate}
                maskClosable={!generated}
                destroyOnClose
                footer={generated
                    ? [
                        <Button key="copy" icon={<CopyOutlined />} onClick={copy}>{t('Copy')}</Button>,
                        <Button key="dl" icon={<DownloadOutlined />} onClick={download}>{t('Download')}</Button>,
                        <Button key="done" type="primary" onClick={closeGenerate}>{t('Done')}</Button>,
                    ]
                    : [
                        <Button key="cancel" onClick={closeGenerate}>{t('Cancel')}</Button>,
                        <Button key="ok" type="primary" loading={saving} onClick={() => genForm.submit()}>{t('Generate')}</Button>,
                    ]}
            >
                {generated ? (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Alert type="warning" showIcon message={t('The private key is shown only once. Download it now.')} />
                        <Input.TextArea readOnly value={generated.privateKey} autoSize={{ minRows: 6, maxRows: 10 }} style={{ fontFamily: 'monospace', fontSize: 12 }} />
                    </Space>
                ) : (
                    <Form form={genForm} layout="vertical" onFinish={submitGenerate}>
                        <Form.Item name="name" label={t('Name')} extra="Ed25519" style={{ marginBottom: 0 }}>
                            <Input maxLength={64} placeholder={t('Optional')} />
                        </Form.Item>
                    </Form>
                )}
            </Modal>

            <Modal title={t('Rename')} open={!!rename} onCancel={() => setRename(null)} onOk={() => renameForm.submit()} confirmLoading={saving} destroyOnClose>
                <Form form={renameForm} key={rename?.id} initialValues={{ name: rename?.name }} layout="vertical" onFinish={submitRename}>
                    <Form.Item name="name" label={t('Name')} rules={[{ required: true, whitespace: true, message: t('Please enter a name') }]} style={{ marginBottom: 0 }}>
                        <Input maxLength={64} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default MySshKeys;
